import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, GraduationCap, Star, Upload, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFaculty = () => {
  const { token } = useAuth();
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject_specialization: [],
    experience_years: 0, qualification: '', bio: '', achievements: [], photo_url: ''
  });
  const [subjectInput, setSubjectInput] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchFaculty(); }, []);

  const fetchFaculty = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/faculty`);
      setFaculty(response.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let submitData = { ...formData };

      if (photoFile) {
        const uploadForm = new FormData();
        uploadForm.append('file', photoFile);
        const uploadRes = await axios.post(
          `${API_URL}/api/uploads?file_type=faculty`,
          uploadForm,
          { headers }
        );
        submitData = { ...submitData, photo_url: uploadRes.data.file_url.replace(/^https?:\/\/[^/]+/, '') };
      }

      if (editingFaculty) {
        await axios.put(`${API_URL}/api/faculty/${editingFaculty.id}`, submitData, { headers });
        toast.success('Faculty updated!');
      } else {
        await axios.post(`${API_URL}/api/faculty`, submitData, { headers });
        toast.success('Faculty added!');
      }
      setDialogOpen(false); resetForm(); fetchFaculty();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this faculty member?')) return;
    try {
      await axios.delete(`${API_URL}/api/faculty/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Faculty deleted!'); fetchFaculty();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const openEdit = (f) => { setEditingFaculty(f); setFormData(f); setPhotoFile(null); setPhotoPreview(null); setDialogOpen(true); };
  const resetForm = () => { setEditingFaculty(null); setFormData({ name: '', email: '', phone: '', subject_specialization: [], experience_years: 0, qualification: '', bio: '', achievements: [], photo_url: '' }); setPhotoFile(null); setPhotoPreview(null); };

  const addSubject = () => {
    if (subjectInput.trim()) {
      setFormData({ ...formData, subject_specialization: [...formData.subject_specialization, subjectInput.trim()] });
      setSubjectInput('');
    }
  };

  return (
    <AdminLayout title="Faculty Management">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Manage faculty members.</p>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-faculty-btn"><Plus className="w-4 h-4 mr-2" /> Add Faculty</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingFaculty ? 'Edit' : 'Add'} Faculty</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
                <div className="space-y-2"><Label>Experience (Years)</Label><Input type="number" value={formData.experience_years} onChange={(e) => setFormData({ ...formData, experience_years: Number(e.target.value) })} /></div>
              </div>
              <div className="space-y-2"><Label>Qualification</Label><Input value={formData.qualification} onChange={(e) => setFormData({ ...formData, qualification: e.target.value })} required /></div>
              <div className="space-y-2">
                <Label>Subject Specialization</Label>
                <div className="flex gap-2">
                  <Input value={subjectInput} onChange={(e) => setSubjectInput(e.target.value)} placeholder="Add subject" />
                  <Button type="button" variant="outline" onClick={addSubject}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.subject_specialization.map((s, i) => (
                    <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setFormData({ ...formData, subject_specialization: formData.subject_specialization.filter((_, idx) => idx !== i) })}>{s} ×</Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2"><Label>Bio</Label><Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} rows={3} /></div>
              <div className="space-y-2">
                <Label>Profile Photo</Label>
                <div
                  className="border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors overflow-hidden"
                  onClick={() => document.getElementById('faculty-photo-input').click()}
                >
                  {photoPreview || formData.photo_url ? (
                    <div className="relative">
                      <img
                        src={photoPreview || formData.photo_url}
                        alt="Preview"
                        className="w-full h-40 object-cover object-top"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPhotoFile(null);
                          setPhotoPreview(null);
                          setFormData({ ...formData, photo_url: '' });
                        }}
                        className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {photoPreview && (
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          {photoFile?.name}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm font-medium">Click to upload photo</span>
                      <span className="text-xs">JPG, PNG, GIF supported</span>
                    </div>
                  )}
                </div>
                <input
                  id="faculty-photo-input"
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPhotoFile(file);
                      setPhotoPreview(URL.createObjectURL(file));
                    }
                    e.target.value = '';
                  }}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    {photoFile ? 'Uploading photo...' : 'Saving...'}
                  </span>
                ) : (
                  <>{editingFaculty ? 'Update' : 'Add'} Faculty</>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">{[1, 2, 3, 4].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-32"></CardContent></Card>)}</div>
      ) : faculty.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {faculty.map((f) => (
            <Card key={f.id} data-testid={`faculty-${f.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                    {f.photo_url ? <img src={f.photo_url.startsWith('http') ? f.photo_url : `${API_URL}${f.photo_url.startsWith('/') ? '' : '/'}${f.photo_url}`} alt={f.name} className="w-full h-full object-cover" /> : <GraduationCap className="w-8 h-8 text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold">{f.name}</h4>
                    <p className="text-sm text-muted-foreground">{f.subject_specialization?.join(', ')}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Star className="w-4 h-4 fill-secondary text-secondary" />
                      <span className="text-sm">{f.rating?.toFixed(1) || '0.0'}</span>
                      <span className="text-sm text-muted-foreground">• {f.experience_years} years</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => openEdit(f)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="outline" size="icon" onClick={() => handleDelete(f.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-16 text-center"><GraduationCap className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold mb-2">No Faculty Yet</h3><p className="text-muted-foreground">Add your first faculty member.</p></CardContent></Card>
      )}
    </AdminLayout>
  );
};

export default AdminFaculty;
